/**
 * [PDP] Buy-box behaviour shared by the product page and the quick view modal: DodoPrime state (prices, sticky bar,
 * delivery quote), the "Add DodoPrime" pre-add, and the delivery module controller. Moved out of
 * snippets/product-template.liquid so the quick view runs the same code: the modal is the product's
 * [data-product-blocks] copied in by theme.js (openModalProduct), whose inline scripts never execute.
 *
 * DDMPdp.init(section) - section = a product section ([data-section-type="product"]): the PDP's, or the one inside
 * a quick view holder. Everything is looked up inside it (theme.js renames ids in the modal copy to *-modal, so
 * payloads are read by data attribute), variant changes come from the section's own `variantChange` event (the
 * document-level variant:change can't tell a quick view on a PDP from the PDP itself), and the sticky bar is found
 * by its form id (a modal form has none). Idempotent per section; started for every product section on load.
 * Loaded on every page (snippets/cart-drawer.liquid, beside ddm-upsell.js): quick view lives on collection pages.
 */
(function () {
  if (window.DDMPdp) return;

  // The attribute, never the `id` property: the form's variant control is named "id" and clobbers it
  function formId(form) { return form ? form.getAttribute('id') : ''; }

  function stickyBar(form) {
    var btn = form && document.querySelector('[data-sticky-atc-btn][data-form-id="' + formId(form) + '"]');
    return btn ? btn.closest('[data-sticky-atc]') : null;
  }

  // The product's availability payload (snippets/pdp-availability-config.liquid), inside the section's blocks
  function availability(section) {
    var node = section.querySelector('script[data-pdp-availability]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (err) { return null; }
  }

  // [PRIME PDP] One Prime state for the buy box. The price block, the sticky bar and the delivery module (below)
  // paint from it, so they always show what the cart will charge. Prime is on when the shopper is a member, the
  // DodoPrime variant is in the cart, or the "Add DodoPrime" box is ticked (it joins the cart with this add;
  // once that add ran, only the cart counts, as in assets/ddm-upsell.js). Delivery: the checkout's rate matrix
  // (snippets/delivery-rate.liquid) on the cart total after this add, membership line included; legacy Prime +
  // gifts (cfg.legacy + the gift tests, see the Liquid above) is the >= threshold tier whatever the total.
  // Follows variant, quantity, Prime box and cart changes (drawer edits, the Prime pre-add, extras).
  function primeState(section, form) {
    var stateEl = section.querySelector('script[data-pdp-prime-state]');
    if (!stateEl) return null;
    var cfg;
    try { cfg = JSON.parse(stateEl.textContent); } catch (err) { return null; }
    var optInBox = section.querySelector('[data-prime-upsell-cb]');
    var s = { inCart: cfg.inCart, total: cfg.cartTotal, price: cfg.price, compare: cfg.compare, cart: { items: cfg.cartItems } };
    var listeners = [];

    function fmt(cents) { return theme.Currency.formatMoney(cents, theme.settings.moneyFormat); }
    function memberPrice(price) { return Math.round(price * (100 - cfg.discount) / 100); }
    function qty() {
      var input = form && (document.querySelector('input[name="quantity"][form="' + formId(form) + '"]') ||
        form.querySelector('[name="quantity"]'));
      return Math.max(1, parseInt(input && input.value, 10) || 1);
    }
    function optIn() { return !!(optInBox && optInBox.checked && !(form && form.__primeUpsellAdded)); }

    function get() {
      var on = cfg.member || s.inCart || optIn();
      var priced = on && cfg.discount > 0;
      var unit = priced ? memberPrice(s.price) : s.price;
      var after = s.total + unit * qty() + (optIn() && !s.inCart ? cfg.primeFee : 0);
      // Delivery: legacy Prime + gifts is the >= threshold tier whatever the total (pre-checkout's legacy branch),
      // decided on the cart after this add
      var legacy = cfg.legacy && legacyGiftsIn(s.cart, unit * qty());
      return {
        priced: priced, price: s.price, compare: s.compare, member: memberPrice(s.price),
        on: on || legacy,
        above: legacy || (on && after >= cfg.threshold),
        gap: Math.ceil((cfg.threshold - after) / 100) * 100 // whole rupees, rounded up: "Rs X more" always crosses
      };
    }
    function rate(p, sameDay, surge) {
      var r = cfg.rates, i = surge ? 1 : 0;
      if (!p.on) return (sameDay ? r.sd : r.std)[i];
      return (sameDay ? r.primeSd : r.primeStd)[p.above ? 1 : 0][i];
    }

    function paintPrice(p) {
      var wrap = section.querySelector('[data-pdp-prime]');
      if (!wrap) return;
      var block = wrap.closest('.product-block--price');
      if (block) block.classList.toggle('pdp-prime-active', p.priced);
      var onEl = wrap.querySelector('[data-pdp-prime-on]');
      var offEl = wrap.querySelector('[data-pdp-prime-off]');
      if (onEl) onEl.hidden = !p.priced;
      if (offEl) offEl.hidden = p.priced;
      wrap.querySelectorAll('[data-prime-price]').forEach(function(el) { el.textContent = fmt(p.member); });
      var was = wrap.querySelector('[data-prime-was]');
      if (was) was.textContent = fmt(p.price);
    }
    function paintSticky(p) {
      var bar = stickyBar(form);
      if (!bar) return;
      var box = bar.querySelector('[data-sticky-price]');
      var amount = bar.querySelector('[data-sticky-amount]');
      var was = bar.querySelector('[data-sticky-was]');
      var compare = bar.querySelector('[data-sticky-compare]');
      if (box) box.classList.toggle('sticky-atc__price--member', p.priced);
      if (amount) amount.textContent = fmt(p.priced ? p.member : p.price);
      if (was) { was.textContent = fmt(p.price); was.hidden = !p.priced; }
      if (compare) { compare.textContent = fmt(p.compare); compare.hidden = p.priced || !(p.compare > p.price); }
      var note = bar.querySelector('[data-sticky-delivery]');
      if (note) {
        var floor = rate(p, false, false);
        note.textContent = p.on && floor === 0
          ? note.getAttribute('data-tmpl-free')
          : note.getAttribute('data-tmpl-from').replace('[rate]', fmt(floor));
      }
    }
    function update() {
      var p = get();
      paintPrice(p);
      paintSticky(p);
      listeners.forEach(function(fn) { fn(p); });
    }

    // Legacy "has gifts" on the cart after this add, as pre-checkout reads it: a gifting line (check-has-gifts)
    // OR a gifting-led cart vertical, beauty folded in (get-cart-vertical, fresh > babybest > gifting on ties).
    // Verticals as those snippets read them (cart-line-vertical.liquid). Unknown line = can't tell = no.
    function legacyGiftsIn(cart, unitTotal) {
      var values = { fresh: 0, babybest: 0, gifting: 0 }, known = true, giftLine = false;
      function count(v, amount) {
        if (v === 'gifting') giftLine = true;
        values[v === 'fresh' || v === 'babybest' ? v : 'gifting'] += amount;
      }
      count(cfg.thisVertical, unitTotal);
      cart.items.forEach(function(item) {
        // this product's lines (any variant, added after the render too) carry its own vertical
        var v = item.product_id === cfg.productId ? cfg.thisVertical : cfg.lineVerticals[item.variant_id];
        if (v) count(v, item.final_line_price); else known = false;
      });
      if (giftLine) return true;
      if (!known) return false;
      return !(values.fresh > 0 && values.fresh >= values.babybest && values.fresh >= values.gifting) &&
        !(values.babybest > 0 && values.babybest >= values.gifting);
    }
    function applyCart(cart) {
      if (!cart || !cart.items) return;
      s.total = cart.total_price;
      s.inCart = cart.items.some(function(i) { return i.variant_id === cfg.primeVariantId; });
      s.cart = cart;
      update();
    }
    // A cart:updated that lands while /cart.js is in flight is newer than that response: `generation` drops it
    var fetching = false, again = false, generation = 0;
    function refetch() {
      if (fetching) { again = true; return; }
      fetching = true;
      var started = generation;
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function(r) { return r.json(); })
        .then(function(cart) { if (started === generation) applyCart(cart); }, function() {})
        .then(function() {
          fetching = false;
          if (again) { again = false; refetch(); }
        });
    }

    // The section's own event: a quick view on a PDP must not move the PDP (document-level variant:change)
    section.addEventListener('variantChange', function(e) {
      var v = e.detail && e.detail.variant;
      if (!v) return;
      s.price = v.price;
      s.compare = v.compare_at_price || 0;
      update();
    });
    document.addEventListener('change', function(e) {
      if (e.target === optInBox || (e.target.name === 'quantity' && form && e.target.form === form)) update();
    });
    // theme.QtySelector's +/- set the value on their own click listeners and fire no event; this runs after them
    document.addEventListener('click', function(e) {
      var wrap = e.target.closest && e.target.closest('.js-qty__adjust') && e.target.closest('.js-qty__wrapper');
      var input = wrap && wrap.querySelector('input[name="quantity"]');
      if (input && form && input.form === form) update();
    });
    document.addEventListener('cart:updated', function(e) {
      if (e.detail && e.detail.cart) {
        generation++;
        applyCart(e.detail.cart);
      } else {
        refetch();
      }
    });
    // Every drawer build, also one recovered after a lost response (no cart:updated): its snapshot is the newest cart
    document.addEventListener('cart:rendered', function(e) {
      var json = e.detail && e.detail.cartJSON, cart = null;
      try { cart = json ? JSON.parse(json) : null; } catch (err) { cart = null; }
      if (cart) {
        generation++;
        applyCart(cart);
      } else {
        refetch();
      }
    });
    document.addEventListener('ajaxProduct:added', refetch);
    document.addEventListener('ajaxProduct:tracked', refetch);

    update();
    return { get: get, rate: rate, fmt: fmt, onChange: function(fn) { listeners.push(fn); } };
  }

  // [PDP P2-2F] DodoPrime upsell — if the checkbox is checked, add the
  // membership variant to the cart BEFORE the PDP submit. Capture-phase listener
  // with stopImmediatePropagation so we run before the theme's bubble-phase ajax
  // submit handler. Re-entry guarded; membership-add failure is silent (drawer
  // cart re-offers Prime when none is present, so a missed add is recoverable).
  // [UPSELL] While a pre-add runs, every press (valid or not) is consumed: only its own
  // resubmit, flagged form.__upsellResubmit for assets/ddm-upsell.js, ends it.
  // Otherwise only a press the theme would accept starts the pre-add: with a required
  // field empty (theme.js addItemFromForm checks the same two fields) the submit goes
  // on to the theme, which shows its field error and posts nothing. Before, such a
  // press added the membership alone and resubmitted later without a new press.
  // The "Complete the gift" extras are captured and locked at THIS press,
  // not after the requests below (DDMUpsell.beginSubmission, assets/ddm-upsell.js),
  // and kept through the resubmit. One pre-add at a time: a second press is ignored.
  // Unchanged pre-existing behaviour: the main add starts only once these requests
  // settle, so a Prime request that never settles leaves Add to cart waiting (the
  // add-on block itself unlocks after the extras deadline and keeps its snapshot).
  // ONE pre-add at a time for the whole page. The PDP and every mounted quick view have their own form; two
  // overlapping pre-adds both read a cart without Prime and both POST it (a duplicate paid line), and theme.js
  // AjaxProduct runs one add at a time page-wide (shared status.loading), so the later resubmit would be dropped
  // silently. While a pre-add runs, every press on any product form is consumed, as one on the same form was.
  var preAddPending = false;
  // theme.js AjaxProduct is busy: it marks the adding form's button .btn--loading exactly while its shared
  // status.loading is set (addItemFromForm, cleared in finish and catch). A pre-add started then would put the
  // paid membership in the cart and its resubmit would be dropped, so it doesn't start.
  function themeAddRunning() {
    return !!document.querySelector('form [data-add-to-cart].btn--loading');
  }
  // A pre-add that hasn't settled after this long releases the page-wide lock and the product add goes on (a stalled
  // request must not block every product form). DDMPdp.preAddDeadlineMs overrides it (tests).
  var PRE_ADD_DEADLINE_MS = 8000;
  // A timed-out request may still land: until it settles, a later pre-add reads the cart but never POSTs the
  // membership again (a duplicate paid line). Without Prime in the cart then, the drawer re-offers it.
  var uncertainAdd = null;

  // stillWanted(): false once the pre-add passed its deadline - a late cart read then never starts the POST
  function addMembership(vid, mayPost, stillWanted) {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var has = (cart && cart.items || []).some(function(i) { return i.variant_id === vid; });
        if (has || !mayPost || !stillWanted()) return null;
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: vid, quantity: 1 })
        })
          .then(function(r) { return r.ok ? r.json() : null; })
          .then(function(item) {
            // GA4/Ads add_to_cart for the silent Prime pre-add (S7C-02).
            // ajaxProduct:tracked, NOT :added — :added would trigger a
            // CartDrawer rebuild that races the main product's rebuild and
            // can overwrite the drawer with Prime-only stale markup.
            if (item) {
              document.dispatchEvent(new CustomEvent('ajaxProduct:tracked', {
                detail: { product: item, quantityAdded: 1 }
              }));
            }
          });
      });
  }

  function primePreAdd(section, form) {
    // Every product form, Prime card or not (members, Fresh, the membership product): its add during another
    // form's pre-add would take theme.js's one add slot and drop that pre-add's product after the membership POST
    form.addEventListener('submit', function(e) {
      if (!preAddPending) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    }, true);
    var upsell = section.querySelector('[data-prime-upsell]');
    if (!upsell) return;
    var checkbox = upsell.querySelector('[data-prime-upsell-cb]');
    var vid = parseInt(upsell.getAttribute('data-prime-vid'), 10) || 0;
    if (!checkbox || !vid || !form) return;

    function missingInput() {
      var text = form.elements.namedItem('properties[Personalization]');
      var option = form.elements.namedItem('properties[Selected Option]');
      return !!((text && !text.disabled && !String(text.value || '').trim()) ||
        (option && !option.disabled && !option.value));
    }

    form.addEventListener('submit', function(e) {
      if (checkbox.checked && !form.__primeUpsellAdded && themeAddRunning()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      if (!checkbox.checked || form.__primeUpsellAdded || missingInput()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      preAddPending = true;

      var extras = window.DDMUpsell;
      if (extras && extras.beginSubmission) extras.beginSubmission(form);

      function proceed() {
        preAddPending = false;
        form.__primeUpsellAdded = true;
        form.__upsellResubmit = true;
        try {
          if (form.requestSubmit) {
            form.requestSubmit();
          } else {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        } catch (err) {
          if (extras && extras.endSubmission) extras.endSubmission(form);
        }
        form.__upsellResubmit = false;
        // [UPSELL] No main add started (the resubmit's validation stopped it, e.g. a field
        // emptied while waiting): the theme fires neither ajaxProduct:added nor :error, so
        // clear the sticky bar's loading state as its error path would (the main button
        // only spins while the main add runs)
        var main = form.querySelector('.add-to-cart');
        if (!main || !main.classList.contains('btn--loading')) {
          var sticky = document.querySelector('[data-sticky-atc-btn][data-form-id="' + form.getAttribute('id') + '"]');
          if (sticky) sticky.classList.remove('btn--loading');
        }
      }

      // Exactly once: on settle, or at the deadline (then a late settle is ignored)
      var finished = false;
      function finish() {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        proceed();
      }
      // Only a request that may POST is ever the uncertain one; a read-only one (made while another is uncertain)
      // never replaces or clears it, so the first stays guarded until it settles
      var mayPost = !uncertainAdd;
      var request = addMembership(vid, mayPost, function() { return !finished; });
      var clearUncertain = function() { if (uncertainAdd === request) uncertainAdd = null; };
      request.then(clearUncertain, clearUncertain);
      var timer = setTimeout(function() {
        if (mayPost) uncertainAdd = request;
        finish();
      }, (window.DDMPdp && window.DDMPdp.preAddDeadlineMs) || PRE_ADD_DEADLINE_MS);
      request.then(finish, finish);
    }, true);
  }

  // [PDP QW1+QW9] Unified delivery module controller — fills the product-aware
  // ETA (today / tomorrow / concrete date) and the weekly-closed line. The
  // sticky bar carries only a static delivery-rate note under the price (no ETA
  // strip). Deliberately NO ticking countdown: the site-wide
  // smart-bar already runs one, so a second timer here read as duplicate/
  // misleading. The module's job is the HONEST, per-product date the smart-bar
  // can't give (it promises "today" even on lead-time products). Runs on the
  // live device clock normalised to Mauritius wall-clock (UTC+4, no DST) so a
  // diaspora timezone can't mis-promise "today"; DDMAvailability so it never
  // promises a closed day. Static block text is the no-JS fallback.
  function delivery(section, form, PdpPrime) {
    var root = section.querySelector('[data-pdp-delivery]');
    if (!root) return;
    var timingEl = root.querySelector('[data-pdp-delivery-timing]');
    var etaEl = root.querySelector('[data-pdp-delivery-eta]');
    var closedEl = root.querySelector('[data-pdp-delivery-closed]');
    var peakEl = root.querySelector('[data-pdp-delivery-peak]');
    var sdVariant = root.querySelector('[data-rates-sameday]');   // absent when lead_hours > 0
    var plainVariant = root.querySelector('[data-rates-plain]');
    var sdTextEl = root.querySelector('[data-sd-text]');
    var otherTextEl = root.querySelector('[data-other-text]');
    var plainTextEl = root.querySelector('[data-plain-text]');
    var gapEl = root.querySelector('[data-pdp-delivery-gap]');
    if (!timingEl || !etaEl) return;

    var DEFAULT_CUTOFF_MIN = 11 * 60 + 30; // 11:30 Mauritius, overridden by vertical policy
    var leadHours = parseInt(root.getAttribute('data-lead-hours'), 10) || 0;
    var lang = document.documentElement.lang || 'en';
    var T = {
      today: root.getAttribute('data-tmpl-today') || 'Delivery today',
      tomorrow: root.getAttribute('data-tmpl-tomorrow') || 'Delivery tomorrow',
      date: root.getAttribute('data-tmpl-date') || 'Earliest delivery [d]',
      closed: root.getAttribute('data-tmpl-closed') || '',
      sameday: root.getAttribute('data-tmpl-sameday') || '',
      other: root.getAttribute('data-tmpl-other') || '',
      otherFree: root.getAttribute('data-tmpl-other-free') || '',
      plain: root.getAttribute('data-tmpl-plain') || '',
      plainFree: root.getAttribute('data-tmpl-plain-free') || '',
      gap: root.getAttribute('data-tmpl-gap') || ''
    };

    // Cutoff comes from vertical_policy (same source pre-checkout uses) — never hardcode.
    function cutoffMinutes(payload) {
      var raw = payload && payload.cutoff;
      var m = /^(\d{1,2}):(\d{2})$/.exec(raw || '');
      if (!m) return DEFAULT_CUTOFF_MIN;
      return (+m[1]) * 60 + (+m[2]);
    }
    // Surge is per-DATE, not "we are in a surge period".
    function isSurge(d, payload) {
      var ranges = (payload && payload.surgeRanges) || [];
      if (!ranges.length) return false;
      var key = iso(d);
      for (var i = 0; i < ranges.length; i++) {
        if (key >= ranges[i].start && key <= ranges[i].end) return true;
      }
      return false;
    }

    // Mauritius is UTC+4 year-round (no DST). Shift the epoch by +4h and read it
    // back through a local-time Date so getHours()/getDay()/iso() carry MU wall
    // clock — matching how DDMAvailability treats its dates.
    function muNow() {
      var u = new Date(Date.now() + 4 * 3600000);
      return new Date(u.getUTCFullYear(), u.getUTCMonth(), u.getUTCDate(), u.getUTCHours(), u.getUTCMinutes(), 0, 0);
    }
    function iso(d) {
      var mo = d.getMonth() + 1, da = d.getDate();
      return d.getFullYear() + '-' + (mo < 10 ? '0' + mo : mo) + '-' + (da < 10 ? '0' + da : da);
    }
    function deliverable(d, DA, payload, cal) {
      if (!DA || !payload) return true;
      var key = iso(d);
      if (cal.disabled && cal.disabled.has(key)) return false;
      if (DA.isWeeklyClosedDay(d, (payload.weeklyClosedDays || []))) return false;
      if (cal.allowed && cal.allowed.size && !cal.allowed.has(key)) return false;
      return true;
    }
    var bar = stickyBar(form);
    var stickyRateEl = bar && bar.querySelector('.sticky-atc__delivery');
    // Null variant (unavailable option combo), sold out, or a non-shipping variant
    // all mean there is no deliverable selection — no honest delivery promise.
    var noDelivery = false;
    function setDeliveryHidden(hide) {
      root.hidden = hide;
      if (stickyRateEl) stickyRateEl.hidden = hide;
    }
    function render() {
      var DA = window.DDMAvailability;
      var payload = DA ? availability(section) : null;
      var variantOut = payload && payload.currentVariant && payload.currentVariant.available === false;
      if (noDelivery || variantOut) {
        // Hide the whole module (ETA *and* rate) — quoting a delivery rate for a
        // variant that can't be delivered is as wrong as quoting a date.
        setDeliveryHidden(true);
        return;
      }
      setDeliveryHidden(false);
      var cal = (DA && payload) ? DA.buildCalendar(payload) : { disabled: new Set(), allowed: null };

      var now = muNow();
      var mins = now.getHours() * 60 + now.getMinutes();
      var pastCutoff = mins >= cutoffMinutes(payload);
      // Same-day is a real "today" option only when the vertical enables it. When it
      // is disabled, today is never deliverable (delivery today == same-day service),
      // so the earliest date starts at tomorrow — the ETA must not say "today" either.
      var sameDayOff = payload && payload.sameDayEnabled === false;
      var skipToday = pastCutoff || (leadHours === 0 && sameDayOff);

      var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      var base = new Date(today);
      base.setDate(base.getDate() + Math.ceil(leadHours / 24) + (skipToday ? 1 : 0));

      var cursor = new Date(base), found = null;
      for (var i = 0; i < 120; i++) {
        if (deliverable(cursor, DA, payload, cal)) { found = new Date(cursor); break; }
        cursor.setDate(cursor.getDate() + 1);
      }
      if (!found) { timingEl.hidden = true; return; }
      timingEl.hidden = false;

      var diff = Math.round((found.getTime() - today.getTime()) / 86400000);
      var etaIsToday = diff <= 0 && leadHours === 0 && !pastCutoff && !sameDayOff;
      var etaText;

      if (etaIsToday) {
        etaText = T.today;
      } else if (diff <= 1) {
        etaText = T.tomorrow;
      } else {
        etaText = T.date.replace('[d]', found.toLocaleDateString(lang, { weekday: 'short', day: 'numeric', month: 'short' }));
      }
      etaEl.textContent = etaText;

      // --- Rate card -------------------------------------------------------
      // The same-day rate is quoted ONLY when same-day is what the shopper would
      // actually get — i.e. the ETA is today (lead_hours == 0, before cutoff, today
      // deliverable, vertical enables it). Once the ETA reads "Delivery tomorrow" or
      // a scheduled date, same-day is not on offer, so quoting its rate is noise.
      // Those states fall back to "Rates from X island-wide".
      var canSameDay = !!sdVariant && !sameDayOff && etaIsToday;
      if (sdVariant) sdVariant.hidden = !canSameDay;
      if (plainVariant) plainVariant.hidden = canSameDay;

      // Each quoted rate is exact for the date it names (PDP Prime state: guest or Prime, below or above the
      // threshold): same-day => today, the plain line => the ETA date; "Other dates" is the non-peak floor.
      // The "Rs X more for FREE delivery" line goes with the plain line only, and never on a peak ETA date:
      // there, crossing the threshold still costs Rs 199.
      var p = PdpPrime && PdpPrime.get();
      if (p) {
        var fmt = PdpPrime.fmt;
        var floor = PdpPrime.rate(p, false, false);
        var plainRate = PdpPrime.rate(p, false, isSurge(found, payload));
        if (canSameDay && sdTextEl) {
          sdTextEl.textContent = T.sameday.replace('[rate]', fmt(PdpPrime.rate(p, true, isSurge(today, payload))));
        }
        if (otherTextEl) {
          otherTextEl.textContent = p.on && floor === 0 ? T.otherFree : T.other.replace('[rate]', fmt(floor));
        }
        if (!canSameDay && plainTextEl) {
          plainTextEl.textContent = p.on && plainRate === 0 ? T.plainFree : T.plain.replace('[rate]', fmt(plainRate));
        }
        if (gapEl) {
          var showGap = p.on && !p.above && !canSameDay && !isSurge(found, payload);
          gapEl.hidden = !showGap;
          if (showGap) gapEl.textContent = T.gap.replace('[amount]', fmt(p.gap));
        }
      }

      // "Other dates from X" is a floor, so peak can't falsify it — the notice is
      // reassurance for the dates we actually point at: the ETA date, and the next
      // day the shopper is most likely to pick. (When same-day is shown, found ===
      // today, so today's surge state is already covered by isSurge(found).)
      if (peakEl) {
        var tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
        peakEl.hidden = !(isSurge(found, payload) || isSurge(tomorrow, payload));
      }

      if (closedEl) {
        var cd = payload && payload.weeklyClosedDaysText;
        if (cd && T.closed) { closedEl.textContent = T.closed.replace('[d]', cd); closedEl.hidden = false; }
        else closedEl.hidden = true;
      }
    }

    section.addEventListener('variantChange', function(e) {
      var v = e.detail && e.detail.variant;
      noDelivery = !v || v.available === false || v.requires_shipping === false;
      render();
    });
    if (PdpPrime) PdpPrime.onChange(render);

    // Re-check on a slow interval so the ETA flips today→tomorrow when the 11:30
    // cutoff passes mid-session. No ticking display, so once a minute is ample.
    if (window.DDMAvailability) {
      render(); setInterval(render, 60000);
    } else {
      document.addEventListener('ddm:availability:ready', function handler() {
        document.removeEventListener('ddm:availability:ready', handler);
        render(); setInterval(render, 60000);
      });
    }
  }

  // Returns the section's Prime state API (get / rate / fmt / onChange), or null
  function init(section) {
    if (!section) return null;
    if (section.__ddmPdp) return section.__ddmPdp.prime;
    var form = section.querySelector('form.product-single__form');
    if (!form) return null;
    var PdpPrime = primeState(section, form);
    section.__ddmPdp = { prime: PdpPrime };
    primePreAdd(section, form);
    delivery(section, form, PdpPrime);
    return PdpPrime;
  }

  // Quick view: the modal's blocks are in place (assets/theme.js openModalProduct)
  document.addEventListener('quickview:loaded', function (e) {
    var id = e.detail && e.detail.productId;
    var modal = id && document.getElementById('QuickShopModal-' + id);
    init(modal && modal.querySelector('[data-section-type="product"]'));
  });

  window.DDMPdp = { init: init };

  // Product sections already on the page (the PDP, a featured product). theme.js fires page:loaded while it runs
  // itself (DOMready calls back at once on an interactive document), i.e. before this later deferred file, so
  // product-template.liquid's own init call finds no DDMPdp on a first load; it covers theme-editor reloads.
  // A quick view shell has no form yet: init skips it until quickview:loaded.
  document.querySelectorAll('[data-section-type="product"]').forEach(init);
})();
